import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import AiLookResults from './AiLookResults';
import { AuthPromptPanel } from './AuthPromptDialog';
import { useAuth } from './AuthContext';
import useModalEscape from './useModalEscape';

function getSourceLabel(sourceName) {
  const value = String(sourceName || '').trim();
  if (!value) return '';
  return value.slice(0, 1).toUpperCase() + value.slice(1);
}

export default function AiLookDialog({ event, open, onClose }) {
  const { language, t } = useI18n();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  useModalEscape(open, onClose);

  function applyAiState(payload) {
    if (payload?.status === 'completed') {
      setResult(payload.data || null);
      setStatus('ready');
      return true;
    }
    if (payload?.status === 'failed') {
      setError(payload.error || payload.run?.error_message || 'ai_matching_failed');
      setStatus('error');
      return true;
    }
    if (payload?.status === 'running') {
      if (payload.data) setResult(payload.data);
      setStatus('loading');
      return false;
    }
    return false;
  }

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    let mounted = true;

    async function matchLook() {
      if (!open || !event?.id) return;

      setStatus('loading');
      setError('');
      setResult(null);

      try {
        const cached = await api.getEventAiMatch(event.id, language).catch(() => null);
        if (!mounted) return;
        if (cached?.status === 'completed' || cached?.status === 'running' || cached?.status === 'failed') {
          applyAiState(cached);
          return;
        }
        if (!isAuthenticated) {
          setError('');
          setStatus('auth_required');
          return;
        }
        const payload = await api.matchEventLook(event.id, language);
        if (mounted) applyAiState(payload);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'ai_matching_failed');
        setStatus('error');
      }
    }

    matchLook();

    return () => {
      mounted = false;
    };
  }, [event?.id, isAuthenticated, language, open]);

  useEffect(() => {
    if (!open || !event?.id || status !== 'loading') return undefined;
    let cancelled = false;

    const timer = window.setInterval(async () => {
      try {
        const payload = await api.getEventAiMatch(event.id, language);
        if (cancelled) return;
        applyAiState(payload);
      } catch {
        // Keep waiting; transient polling failures should not interrupt the user.
      }
    }, 4500);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [event?.id, language, open, status]);

  async function retryMatch() {
    if (!event?.id) return;
    if (!isAuthenticated) {
      setStatus('auth_required');
      return;
    }
    setStatus('loading');
    setError('');

    try {
      const payload = await api.matchEventLook(event.id, language, { force: true });
      applyAiState(payload);
    } catch (err) {
      setError(err.message || 'ai_matching_failed');
      setStatus('error');
    }
  }

  if (!open || !event) return null;

  const sourceLabel = getSourceLabel(event.source_name);

  return (
    <div className="ct-ai-overlay" role="presentation" onMouseDown={onClose}>
      <section
        className="ct-ai-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ct-ai-dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button type="button" className="ct-ai-close" onClick={onClose} aria-label={t('aiDialog.close')}>
          <i className="ph ph-x"></i>
        </button>

        <div className="ct-ai-hero">
          <div className="ct-ai-media">
            {event.image_url ? (
              <img src={event.image_url} alt={event.title} />
            ) : (
              <span className="ct-ai-media-fallback">
                <i className="ph ph-image"></i>
              </span>
            )}
          </div>
          <div className="ct-ai-copy">
            <p className="ct-kicker">{t('aiDialog.kicker')}</p>
            <h2 id="ct-ai-dialog-title">{t('aiDialog.title')}</h2>
            <p>{t('aiDialog.copy')}</p>
            <div className="ct-ai-tags" aria-label={t('aiDialog.tagsLabel')}>
              {sourceLabel ? <span>{sourceLabel}</span> : null}
              <span>{t('aiDialog.tagThreeLanes')}</span>
              <span>{t('aiDialog.tagSmartMatch')}</span>
            </div>
          </div>
        </div>

        {status === 'auth_required' ? (
          <div className="ct-ai-inline-auth">
            <AuthPromptPanel
              title={t('authPrompt.matchTitle')}
              copy={t('authPrompt.matchCopy')}
              className="ct-ai-auth-panel"
            />
          </div>
        ) : (
          <AiLookResults status={status} result={result} error={error} context="dialog" onRetry={retryMatch} />
        )}

        <div className="ct-ai-footer">
          <Link to={`/events/${event.id}`} className="ct-ai-primary" onClick={onClose}>
            {t('aiDialog.openLook')}
          </Link>
        </div>
      </section>
    </div>
  );
}
